import type { User } from '@/types/user'
import { useTeamStore } from '@/stores/teamStore'
import type { ActionType } from '@/types/role'
import type { TeamRole, TeamUser } from '@/types/teamUser'

// RBAC role names that map 1:1 onto a team-scoped membership role. Team
// membership now lives in the RBAC role context (`role.context.teams`) - the
// legacy team_users join table is gone - so this is the source we rebuild the
// `user.teamUsers` list from.
const RBAC_ROLE_TO_TEAM_ROLE: Record<string, TeamRole> = {
  team_leader: 'team_leader',
  scrum_master: 'scrum_master',
  team_member: 'team_member',
}

// deriveTeamMembershipsFromRoles reconstructs the user's team memberships
// (teamId + role) from their RBAC roles' `context.teams`. This replaces the old
// `/users/{id}/team_users` fetch: that endpoint now returns a team's member
// *users*, not this user's memberships, so consumers that need teamId/role
// (hasActiveTeam, getUserHomePath, the router guards, teamStore loading) must
// source them here instead.
export function deriveTeamMembershipsFromRoles(user: User | null): TeamUser[] {
  if (!user?.roles) {
    return []
  }
  const memberships: TeamUser[] = []
  const seen = new Set<string>()
  for (const role of user.roles) {
    const teamRole = RBAC_ROLE_TO_TEAM_ROLE[role.name as string]
    if (!teamRole) {
      continue
    }
    const teams = (role as any).context?.teams
    if (!Array.isArray(teams)) {
      continue
    }
    for (const rawTeamId of teams) {
      const teamId = String(rawTeamId)
      const key = `${teamId}:${teamRole}`
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      memberships.push({
        id: `${user.id}:${teamId}:${teamRole}`,
        teamId,
        userId: user.id,
        role: teamRole,
      })
    }
  }
  return memberships
}


export function getFullName(user: User | null): string {
  if (!user) return 'Unknown User';

  const first = user.firstName ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1) : '';
  const last = user.lastName ? user.lastName.charAt(0).toUpperCase() + user.lastName.slice(1) : '';

  // Join only non-empty parts
  const fullName = [first, last].filter(Boolean).join(' ');
  if (fullName) return fullName;

  if (user.email) return user.email;

  return 'Unknown User';
}


export function hasOrganization(user: User | null): boolean {
  if (!user) {
    return false
  }

  if (user.organization && user.organization.id !== '00000000-0000-0000-0000-000000000000') {
    return true
  }

  return false
}

export function hasTeams(user: User | null): boolean {
  if (!user) {
    return false
  }
  if (!user.teamUsers) {
    return false
  }
  if (user.teamUsers.length === 0) {
    return false
  }
  return true
}

export function isTeamLeader(user: User | null): boolean {
  if (!user) {
    return false
  }
  // Check new RBAC roles system first
  if (user.roles && user.roles.length > 0) {
    for (const role of user.roles) {
      if (role.name === 'team_leader') {
        return true
      }
    }
  }
  // Fallback: check legacy teamUsers system
  if (!user.teamUsers) {
    return false
  }
  for (const teamUser of user.teamUsers) {
    if (teamUser.role === 'team_leader') {
      return true
    }
  }
  return false
}

export function isScrumMaster(user: User | null): boolean {
  if (!user) {
    return false
  }
  // Check new RBAC roles system first
  if (user.roles && user.roles.length > 0) {
    for (const role of user.roles) {
      if (role.name === 'scrum_master') {
        return true
      }
    }
  }
  // Fallback: check legacy teamUsers system
  if (!user.teamUsers) {
    return false
  }
  for (const teamUser of user.teamUsers) {
    if (teamUser.role === 'scrum_master') {
      return true
    }
  }
  return false
}

export function isTeamMember(user: User | null): boolean {
  if (!user) {
    return false
  }
  if (!user.teamUsers) {
    return false
  }
  if (user.teamUsers.length === 0) {
    return false
  }
  return true
}

export function isOrgLeader(user: User | null): boolean {
  if (!user) {
    return false
  }
  // Check new RBAC roles system first
  if (user.roles && user.roles.length > 0) {
    for (const role of user.roles) {
      if (role.name === 'org_leader') {
        return true
      }
    }
  }
  return false
}

export function isTPAAdmin(user: User | null): boolean {
  if (!user) {
    return false
  }

  // RBAC is the source of truth: a tpa_admin assignment grants admin power
  // regardless of which organization the user belongs to, mirroring how the
  // backend gates /v2/admin endpoints via `RoleMiddleware([]Role{TPAAdmin, …})`.
  // The legacy organizationUser.roles fallback was removed with the
  // organization_users table - the endpoint that populated it returns a plain
  // User now, so that path could never fire again.
  if (user.roles && user.roles.length > 0) {
    for (const role of user.roles) {
      if (role.name === 'tpa_admin') {
        return true
      }
    }
  }

  return false
}

// isDeveloper is deliberately NARROWER than useIsPrivilegedViewer (which also
// admits tpa_admin): it gates development-only tooling that fabricates data,
// which a tpa_admin working in a real organization must not be able to trigger.
// Mirrors the backend's `RoleMiddleware([]Role{Developer})` on the
// /v2/helpers/sessions/* group.
export function isDeveloper(user: User | null): boolean {
  if (!user?.roles) {
    return false
  }
  return user.roles.some((role) => role.name === 'developer')
}

export function hasActiveTeam(user: User | null): boolean {
  if (!user) {
    return false
  }
  if (!user.teamUsers) {
    return false
  }
  const teamStore = useTeamStore()

  for (const teamUser of user.teamUsers) {
    // Get team info from store
    if (!teamUser.teamId) {
      continue
    }
    const team = teamStore.teams[teamUser.teamId]
    if (!team) {
      continue
    } else if (team && team.activated) {
      return true
    }
  }

  return false
}

export function hasPermission(user: User | null, entity: string, action: ActionType): boolean {
  if (!user || !user.roles) return false;

  return user.roles.some(role =>
    role.permissions?.some(permission =>
      permission.entity === entity &&
      permission.actions?.some(pAction => pAction.name === action)
    )
  );
}

export function hasModule(user: User | null, moduleName: string): boolean {
  if (!user || !user.roles) return false;

  return user.roles.some(role =>
    role.modules?.some(module => module.name === moduleName)
  );
}

export function hasView(user: User | null, viewPath: string): boolean {
  if (!user || !user.roles) return false;

  return user.roles.some(role =>
    role.modules?.some(module =>
      module.views?.some(view => view.path === viewPath)
    )
  );
}

export function getAvailableActions(user: User | null, entity: string): ActionType[] {
  if (!user || !user.roles) return [];

  const actions = new Set<ActionType>();
  user.roles.forEach(role => {
    role.permissions?.forEach(permission => {
      if (permission.entity === entity) {
        permission.actions?.forEach(action => {
          actions.add(action.name);
        });
      }
    });
  });

  return Array.from(actions);
}
